import { Box, BoxProps, Dialog, Stack, styled, Tab, Tabs, Typography } from '@mui/material';

const MEDIUM_SCREEN_SIDEBAR_WIDTH = 350;
const SMALL_SCREEN_SIDEBAR_WIDTH = 370;

export const ModalSmall = styled(Dialog)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  [theme.breakpoints.down('lg')]: {
    alignItems: 'start',
  },

  '& .MuiDialog-paper > form': {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 100%',
    height: '100%',
  },

  '& .MuiDialog-paperFullWidth': {
    width: 700,

    [theme.breakpoints.only('lg')]: {
      width: 600,
    },
    [theme.breakpoints.down('lg')]: {
      width: '100vw',
    },
  },

  '& .withRightSidebarOpen': {
    [theme.breakpoints.down('lg')]: {
      width: `calc(100vw - ${MEDIUM_SCREEN_SIDEBAR_WIDTH}px)`,
    },
    [theme.breakpoints.down('md')]: {
      width: `calc(100vw - ${SMALL_SCREEN_SIDEBAR_WIDTH}px)`,
    },
  },
}));

export const ModalConfirm = styled(Dialog)<{ width?: number }>(({ theme, width = 600 }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: theme.zIndex.tooltip,

  '& .MuiDialog-paper > form': {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 100%',
    height: 'unset',
  },

  '& .MuiDialog-paperFullWidth': {
    width,
    height: 'unset',
  },
}));

export const ModalOpaque = styled(ModalConfirm)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: theme.palette.common.lightBg,
  },
  '& .MuiDialog-paper': {
    boxShadow: '0px 30px 50px rgba(37, 39, 90, 0.1)',
  },
}));

ModalSmall.defaultProps = {
  fullWidth: true,
};

export const ModalLarge = styled(ModalSmall)(({ theme }) => ({
  '& .MuiDialog-paperFullWidth': {
    width: 1000,

    [theme.breakpoints.down('lg')]: {
      width: '100vw',
    },
  },
}));

export interface ModalPanelProps extends BoxProps {
  withBorderTop?: boolean;
  withBorderBottom?: boolean;
  active?: boolean;
  cursor?: boolean;
  disabled?: boolean;
  flat?: boolean;
  fullHeight?: boolean;
}

export const ModalPanel = styled(Box, {
  shouldForwardProp: (prop) =>
    !(
      [
        'withBorderTop',
        'withBorderBottom',
        'active',
        'cursor',
        'disabled',
        'flat',
        'fullHeight',
      ] as PropertyKey[]
    ).includes(prop),
})<ModalPanelProps>(
  ({ theme, withBorderTop, withBorderBottom, active, cursor, disabled, flat, fullHeight }) => ({
    flex: fullHeight ? '1 1 100%' : '0 0 auto',
    padding: flat ? 0 : theme.spacing(2.5),
    backgroundColor: active ? theme.palette.background.paper : theme.palette.background.default,
    borderTop: withBorderTop ? theme.mixins.borderValue() : undefined,
    borderBottom: withBorderBottom ? theme.mixins.borderValue() : undefined,
    overflow: 'auto',
    cursor: cursor ? 'pointer' : undefined,
    ...(disabled && {
      pointerEvents: 'none',
    }),
  }),
);

export const ModalSubpanel = styled(ModalPanel)(({ theme }) => ({
  paddingLeft: 0,
  paddingRight: 0,
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}));

export const ModalTitleMultiLine = styled(Typography)();

const ModalTitleDefaultProps = {
  variant: 'h1',
  className: 'ModalTitle',
} as const;

ModalTitle.defaultProps = ModalTitleDefaultProps;
ModalTitleMultiLine.defaultProps = ModalTitleDefaultProps;

export const ModalMain = styled(Stack)(({ theme }) => ({
  flex: '1 1 auto',
  flexDirection: 'row',
  alignItems: 'stretch',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
}));

export const MODAL_SIDEBAR_WIDTH = 200;

export const ModalSideBar = styled(ModalPanel)(({ theme }) => ({
  flex: `0 0 ${MODAL_SIDEBAR_WIDTH}px`,
  height: '100%',
  padding: theme.spacing(2, 1.25),
  borderRight: theme.mixins.borderValue(),
}));

ModalSideBar.defaultProps = {
  active: true,
};

export const ModalContent = styled(ModalPanel)(({ theme }) => ({
  flex: '1 1 auto',
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2.5),
}));

export const ModalContentStickyWrapper = styled(Stack)({
  minHeight: '100%',
});

export const ModalContentStickyTitle = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(-2.5),
  margin: theme.spacing(-2.5, -2.5, 0),
  padding: theme.spacing(2.5, 2.5, 0),
  background: theme.palette.background.default,
  zIndex: theme.zIndex.appBar,
}));

export const ModalFooter = styled(ModalPanel)(({ theme }) => ({
  display: 'flex',
  flex: '0 0 auto',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(),
  paddingBottom: theme.spacing(),
  marginTop: -1,
}));

ModalFooter.defaultProps = {
  withBorderTop: true,
};

export const ModalTabs = styled(Tabs)(({ theme }) => ({
  minHeight: theme.spacing(3.5),

  '& .MuiTabs-flexContainer': {
    borderBottom: '2px solid',
    borderBottomColor: theme.palette.divider,
  },

  '& .MuiTab-root': {
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: theme.spacing(3.5),
    padding: theme.spacing(0, 0.5),
    marginBottom: -2,
    borderBottom: '2px solid transparent',

    '&:first-of-type': {
      paddingLeft: theme.spacing(2.5),
    },

    '&:last-of-type': {
      paddingRight: theme.spacing(2.5),
    },

    '&:not(:last-of-type)': {
      marginRight: 0,
      paddingRight: theme.spacing(1),
    },
  },
}));

ModalTabs.defaultProps = {
  value: false,
};

export interface ModalTabProps {
  active?: boolean;
  completed?: boolean;
}

export const ModalTab = styled(Tab)<ModalTabProps>(({ theme, active, completed }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  pointerEvents: 'none',

  '&&': {
    borderBottomColor: completed ? theme.palette.primary.main : 'transparent',
  },
}));

ModalTab.defaultProps = {
  iconPosition: 'end',
};
